import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiArrowRight, FiCreditCard, FiGift, FiGrid, FiList, FiSearch, FiSmartphone, FiTv } from "react-icons/fi";
import { MdOutlineAccountBalanceWallet } from "react-icons/md";
import { FaTicketAlt } from "react-icons/fa";
import ThemedPageFrame from "./ThemedPageFrame";
import { MyContext } from "../../Context/MyProvider";
import { requireLogin } from "../../utils/auth";
import "../../Style/anna-wallet.css";

const paymentServices = [
    { service_slug: "bill_payment", service_name: "BBPS / Bill Payments", path: "/pay-bills", icon: FiCreditCard },
    { service_slug: "pre_mobile", service_name: "Mobile Recharge", path: "/mobile-recharge", icon: FiSmartphone },
    { service_slug: "dth", service_name: "DTH Recharge", path: "/dth-recharge", icon: FiTv },
];

const accountActions = [
    { label: "My Wallet", description: "View your wallet and balance", path: "/wallet", icon: MdOutlineAccountBalanceWallet, protected: true },
    { label: "Offers", description: "Explore available deals and offers", path: "/offers", icon: FiGift, protected: false },
    { label: "Bookings", description: "Manage your tickets and bookings", path: "/booking", icon: FaTicketAlt, protected: true },
    { label: "Transactions", description: "Review your wallet transaction history", path: "/wallet#transactions", icon: FiList, protected: true },
];

export default function AnnaWallet() {
    const navigate = useNavigate();
    const { setIsLoginShow } = useContext(MyContext);
    const [services, setServices] = useState([]);
    const [status, setStatus] = useState("loading");
    const [attempt, setAttempt] = useState(0);
    const [query, setQuery] = useState("");

    useEffect(() => { window.scrollTo(0, 0); }, []);

    useEffect(() => {
        const controller = new AbortController();
        setStatus("loading");
        axios.get("https://shop.nabhiverse.com/api/v1/service/list", { signal: controller.signal })
            .then(({ data }) => {
                if (!Array.isArray(data?.response)) throw new Error("Invalid service list");
                setServices(data.response.filter((service) => service?.is_active));
                setStatus("ready");
            })
            .catch(() => { if (!controller.signal.aborted) setStatus("error"); });
        return () => controller.abort();
    }, [attempt]);

    const openPage = (path, needsLogin = true, serviceId) => {
        const destination = serviceId ? `${path}?serviceId=${encodeURIComponent(serviceId)}` : path;
        if (needsLogin && !requireLogin(setIsLoginShow, destination)) return;
        navigate(destination, { state: { serviceId } });
    };
    const matchesQuery = (label) => label.toLowerCase().includes(query.trim().toLowerCase());
    const otherServices = services.filter((service) =>
        !paymentServices.some((payment) => payment.service_slug === service.service_slug)
    );

    return (
        <ThemedPageFrame activePath="/1-anna-wallet" eyebrow="Payments & services" title="1 Anna Wallet"
            subtitle="Bills, recharges, offers and bookings — together in one place." className="anna_wallet_page">
            <Link className="anna_back_link" to="/">← Back to home</Link>
            <section className="anna_account_grid" aria-label="Wallet and account">
                {accountActions.map((action) => {
                    const Icon = action.icon;
                    return <button key={action.label} type="button" className="anna_account_card"
                        onClick={() => openPage(action.path, action.protected)}>
                        <span className="anna_icon"><Icon /></span>
                        <strong>{action.label}</strong><span>{action.description}</span>
                        <FiArrowRight className="anna_card_arrow" aria-hidden="true" />
                    </button>;
                })}
            </section>
            <div className="anna_services_heading">
                <div><h2>Services</h2><p>Choose a service to get started.</p></div>
                <label className="anna_search"><FiSearch aria-hidden="true" />
                    <input aria-label="Search services" placeholder="Search services" value={query} onChange={(event) => setQuery(event.target.value)} />
                </label>
            </div>
            <section className="anna_service_section" aria-labelledby="anna_bbps_title">
                <h3 id="anna_bbps_title">BBPS & Recharges</h3>
                <div className="anna_services_grid">
                    {paymentServices.filter((service) => matchesQuery(service.service_name)).map((service) => {
                        const Icon = service.icon;
                        const apiService = services.find((item) => item.service_slug === service.service_slug);
                        return <button type="button" className="anna_service_card" key={service.service_slug}
                            disabled={!apiService?.service_id && service.service_slug !== "bill_payment"}
                            onClick={() => openPage(service.path, true, apiService?.service_id)}>
                            <span className="anna_icon"><Icon /></span><strong>{service.service_name}</strong>
                            <span>{!apiService?.service_id && service.service_slug !== "bill_payment" ? (status === "loading" ? "Loading…" : "Currently unavailable") : <>Pay now <FiArrowRight aria-hidden="true" /></>}</span>
                        </button>;
                    })}
                </div>
            </section>
            <section className="anna_service_section" aria-labelledby="anna_more_title" hidden>
                <h3 id="anna_more_title">More services</h3>
                {status === "loading" && <p role="status">Loading services…</p>}
                {status === "error" && <div className="anna_service_message" role="alert">Unable to load additional services. <button type="button" onClick={() => setAttempt((value) => value + 1)}>Try again</button></div>}
                <div className="anna_services_grid">
                    {otherServices.filter((service) => matchesQuery(service.service_name || "")).map((service) => (
                        <button type="button" disabled className="anna_service_card" key={service.id || service.service_slug}>
                            <span className="anna_icon"><FiGrid /></span><strong>{service.service_name}</strong><span>Coming soon</span>
                        </button>
                    ))}
                </div>
                {status === "ready" && !otherServices.some((service) => matchesQuery(service.service_name || "")) &&
                    <p className="anna_service_message">{query ? "No additional services match your search." : "More services will appear here when available."}</p>}
            </section>
        </ThemedPageFrame>
    );
}
